import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { TextField } from 'components/Field/TextField';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { PageEmpty } from 'components/PageEmpty';
import { PageHeader } from 'components/PageHeader';
import { useAlert } from 'hooks/useAlert';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useSelector } from 'hooks/useSelector';
import { cloneDeep, isEqual } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  doSettingsIntegrationUpdate,
  NAVIGATE_MODAL_BACK,
} from 'store/actions';
import { selectSettingsIntegration } from 'store/remote/selectors';
import { Integration } from 'types/Integrations';

export const IntegrationEditModal: React.FC<
  ModalScreenProps<ModalScreens.SettingsIntegrationEdit>
> = ({ route }) => {
  const dispatch = useDispatch();
  const alert = useAlert();
  const confirmUnsaved = useConfirmUnsaved();
  const integration: Integration | undefined = useSelector((state) =>
    selectSettingsIntegration(state, route.params.integrationId),
  );
  const [draft, setDraft] = React.useState<Integration | undefined>(() =>
    integration ? cloneDeep(integration) : undefined,
  );
  const [configurationInputs, setConfigurationInputs] = React.useState<
    Record<string, string>
  >(() =>
    Object.fromEntries(
      (integration?.configurations || []).map((option) => [
        option.key,
        option.value === null ? '' : String(option.value),
      ]),
    ),
  );

  React.useEffect(() => {
    if (integration) {
      setDraft(cloneDeep(integration));
      setConfigurationInputs(
        Object.fromEntries(
          integration.configurations.map((option) => [
            option.key,
            option.value === null ? '' : String(option.value),
          ]),
        ),
      );
    }
  }, [integration]);

  if (!integration || !draft) {
    return <PageEmpty text="Integration Not Found" />;
  }

  const isDirty = !isEqual(integration, draft);

  const save = () => {
    const missingRequired = draft.configurations.find(
      (option) =>
        option.required &&
        (option.value === null ||
          (typeof option.value === 'string' && !option.value.trim())),
    );
    if (missingRequired) {
      alert({ text: `${missingRequired.name} is required` });
      return;
    }
    const invalidNumber = draft.configurations.find(
      (option) =>
        option.valueType === 'number' &&
        option.value !== null &&
        !Number.isFinite(option.value),
    );
    if (invalidNumber) {
      alert({ text: `${invalidNumber.name} must be a valid number` });
      return;
    }

    dispatch(
      doSettingsIntegrationUpdate({
        organisationId: route.params.organisationId,
        integrationId: integration.uid,
        update: {
          integrationName: draft.integrationName?.trim() || null,
          enabled: draft.enabled,
          options: draft.options.map(({ key, enabled }) => ({ key, enabled })),
          configurations: draft.configurations.map(({ key, value }) => ({
            key,
            value,
          })),
        },
      }),
    );
  };

  const close = () => {
    if (isDirty) {
      confirmUnsaved({
        onSave: save,
        onDiscard: () =>
          dispatch(NAVIGATE_MODAL_BACK([ModalScreens.SettingsIntegrationEdit])),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK([ModalScreens.SettingsIntegrationEdit]));
  };

  const updateConfigurationValue = (
    key: string,
    value: string | number | boolean | null,
  ) =>
    setDraft((current) =>
      current
        ? {
            ...current,
            configurations: current.configurations.map((option) =>
              option.key === key ? { ...option, value } : option,
            ),
          }
        : current,
    );

  return (
    <Page
      scrollView
      header={
        <PageHeader
          title={draft.integrationName || draft.integrationPartner}
          secondaryTitle={draft.integrationPartner}
          onPressCancelButton={close}
          onPressSaveButton={isDirty ? save : null}
        />
      }
    >
      <Card label="Integration">
        <TextField
          label="Custom name"
          placeholder="Custom name (optional)"
          maxLength={256}
          value={draft.integrationName || ''}
          onChange={(integrationName) =>
            setDraft((current) =>
              current ? { ...current, integrationName } : current,
            )
          }
        />
        <CardRow
          label="Enabled"
          label2="Enable this integration after all required configuration is set"
          switchValue={draft.enabled}
          onSwitchChange={() =>
            setDraft((current) =>
              current ? { ...current, enabled: !current.enabled } : current,
            )
          }
        />
      </Card>

      {!!draft.options.length && (
        <Card label="Features">
          {draft.options.map((option) => (
            <CardRow
              key={option.key}
              label={option.name}
              label2={option.description}
              label2NumberOfLines={3}
              switchValue={option.enabled}
              onSwitchChange={() =>
                setDraft((current) =>
                  current
                    ? {
                        ...current,
                        options: current.options.map((item) =>
                          item.key === option.key
                            ? { ...item, enabled: !item.enabled }
                            : item,
                        ),
                      }
                    : current,
                )
              }
            />
          ))}
        </Card>
      )}

      {!!draft.configurations.length && (
        <Card label="Configuration">
          {draft.configurations.map((option) =>
            option.valueType === 'boolean' ? (
              <CardRow
                key={option.key}
                label={option.name}
                label2={option.description}
                required={option.required}
                switchValue={option.value === true}
                onSwitchChange={() =>
                  updateConfigurationValue(option.key, option.value !== true)
                }
              />
            ) : (
              <CardRow
                key={option.key}
                label={option.name}
                label2={option.description}
                label2NumberOfLines={3}
                labelAlignTop
                required={option.required}
              >
                <TextField
                  label={option.name}
                  required={option.required}
                  placeholder={`Set ${option.name.toLowerCase()}`}
                  value={
                    configurationInputs[option.key] ??
                    (option.value === null ? '' : String(option.value))
                  }
                  inputMode={
                    option.valueType === 'number' ? 'decimal' : undefined
                  }
                  onChange={(value) => {
                    setConfigurationInputs((current) => ({
                      ...current,
                      [option.key]: value,
                    }));
                    updateConfigurationValue(
                      option.key,
                      value === ''
                        ? null
                        : option.valueType === 'number'
                          ? Number(value)
                          : value,
                    );
                  }}
                />
              </CardRow>
            ),
          )}
        </Card>
      )}
    </Page>
  );
};
